import { type ReactNode, useCallback, useMemo } from "react";
import { ConvexProviderWithAuth, type ConvexReactClient } from "convex/react";

interface GetAccessTokenOptions {
  forceRefresh?: boolean;
}

interface UseAuthResult {
  isLoading: boolean;
  user: unknown | null;
  getAccessToken: (options?: GetAccessTokenOptions) => Promise<string | null>;
}

interface UseAuth {
  (): UseAuthResult;
}

interface ConvexProviderWithAuthKitProps {
  children: ReactNode;
  client: ConvexReactClient;
  useAuth: UseAuth;
}

interface FetchAccessTokenOptions {
  forceRefreshToken: boolean;
}

/**
 * Provides a Convex client authenticated via WorkOS AuthKit.
 */
export function ConvexProviderWithAuthKit({
  children,
  client,
  useAuth,
}: ConvexProviderWithAuthKitProps) {
  const useAuthFromWorkOS = useUseAuthFromAuthKit(useAuth);

  return (
    <ConvexProviderWithAuth client={client} useAuth={useAuthFromWorkOS}>
      {children}
    </ConvexProviderWithAuth>
  );
}

function useUseAuthFromAuthKit(useAuth: UseAuth) {
  return useMemo(
    () =>
      function useAuthFromWorkOS() {
        const { isLoading, user, getAccessToken } = useAuth();

        const fetchAccessToken = useCallback(
          async ({ forceRefreshToken }: FetchAccessTokenOptions) => {
            try {
              const token = await getAccessToken(
                forceRefreshToken ? { forceRefresh: true } : undefined
              );
              return token;
            } catch {
              return null;
            }
          },
          [getAccessToken]
        );

        return useMemo(
          () => ({
            isLoading,
            isAuthenticated: !!user,
            fetchAccessToken,
          }),
          [isLoading, user, fetchAccessToken]
        );
      },
    [useAuth]
  );
}
